import { skipToken } from '@reduxjs/toolkit/dist/query/react';
import { useMemo } from 'react';
import { useGetFinalizedHoursQuery } from 'store/api/dashboard';
import { PieChartItem } from 'types/dashboard';
import { CHART_COLORS } from 'utils/chartColors';
import { t } from 'utils/getTranslations';

interface Props {
    dateFrom: string;
    dateTo: string;
    /** Home's slice order and series colours, which exist only in the new design. */
    home?: boolean;
}

/** Index-aligned with Home's slice order below. */
const HOME_COLORS = [
    'var(--color-hours-client)',
    'var(--color-hours-pro-bono)',
    'var(--color-hours-bd)',
    'var(--color-hours-offers)',
    'var(--color-hours-internal)',
];

const useFinalizedHours = ({ dateFrom, dateTo, home = false }: Props) => {
    const {
        data: finalizedHoursData,
        isLoading: isLoadingFinalizedHours,
        isFetching: isFetchingFinalizedHours,
    } = useGetFinalizedHoursQuery(!(dateFrom || dateTo) ? skipToken : { dateFrom, dateTo });

    const finalizedHours = useMemo<PieChartItem[]>(() => {
        const client = {
            id: 'client',
            value: finalizedHoursData?.clientHours || 0,
            label: t('finalized-hours.client'),
            tooltip: 'finalized-hours.tooltips.client',
        };
        const proBono = {
            id: 'pro-bono',
            value: finalizedHoursData?.proBonoHours || 0,
            label: t('finalized-hours.pro-bono'),
            tooltip: 'finalized-hours.tooltips.pro-bono',
        };
        const offers = {
            id: 'offers',
            value: finalizedHoursData?.offerHours || 0,
            label: t('finalized-hours.offers'),
            tooltip: 'finalized-hours.tooltips.offers',
        };
        const internal = {
            id: 'internal',
            value: finalizedHoursData?.internalHours || 0,
            label: t('finalized-hours.internal'),
            tooltip: 'finalized-hours.tooltips.internal',
        };
        const bdEfforts = {
            id: 'bd-efforts',
            value: finalizedHoursData?.bdEffortHours || 0,
            label: t('finalized-hours.bd-efforts'),
            tooltip: 'finalized-hours.tooltips.bd-efforts',
        };
        // On Home, Business development sits third so that no two touching slices are close colours.
        const slices = home
            ? [client, proBono, bdEfforts, offers, internal]
            : [client, proBono, offers, internal, bdEfforts];
        const colors = home ? HOME_COLORS : CHART_COLORS;
        return slices.map((slice, index) => ({ ...slice, color: colors[index] }));
    }, [finalizedHoursData, home]);

    const isAllZero = finalizedHours.every((a) => a.value === 0);

    const isLoadingWithNoPreviousData = !finalizedHoursData && isLoadingFinalizedHours;

    return {
        finalizedHours,
        finalizedHoursData: finalizedHoursData,
        isLoadingWithNoPreviousData,
        isLoading: isLoadingFinalizedHours || isFetchingFinalizedHours,
        isAllZero,
    };
};

export default useFinalizedHours;
